/**
 * RFC 162: resolve the account tier right after sign-in without letting a weak
 * connection hold every page behind the tier check.
 *
 * The server read is bounded; when it is slow or fails, the tier last stored on
 * this device for THIS user is used. This only shapes the UI — the backend
 * enforces entitlements on every request, and the periodic subscription check
 * corrects the tier once the connection recovers.
 */
import { supabase } from '@/integrations/supabase/client';
import { getUserTier, storeUserTier } from '@/lib/offline/db';
import { withReadDeadline } from '@/lib/readDeadline';

export const INITIAL_TIER_DEADLINE_MS = 3000;

export interface InitialTierDeps {
  readServerTier: (userId: string, signal: AbortSignal) => PromiseLike<string | null>;
  readDeviceTier: (userId: string) => Promise<string | null>;
  storeDeviceTier: (userId: string, tier: string) => Promise<unknown>;
}

const defaultDeps: InitialTierDeps = {
  readServerTier: async (userId, signal) => {
    const { data, error } = await supabase
      .from('user_tiers')
      .select('tier')
      .eq('user_id', userId)
      .abortSignal(signal)
      .maybeSingle();
    if (error) throw error;
    return (data?.tier as string | undefined) ?? null;
  },
  readDeviceTier: getUserTier,
  storeDeviceTier: storeUserTier,
};

export async function resolveInitialTier(
  userId: string,
  deps: InitialTierDeps = defaultDeps,
): Promise<string | null> {
  try {
    const serverTier = await withReadDeadline(
      (signal) => deps.readServerTier(userId, signal),
      { ms: INITIAL_TIER_DEADLINE_MS },
    );
    if (serverTier) {
      deps.storeDeviceTier(userId, serverTier).catch(() => undefined);
      return serverTier;
    }
  } catch (error) {
    console.warn('[Auth] Initial tier read slow or failed, using device tier:', error);
  }
  try {
    return await deps.readDeviceTier(userId);
  } catch {
    return null;
  }
}
